import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/index';

/**
 * Calendar screen shared interaction state (feature 107).
 *
 * Holds business-critical/shared UI state that previously lived as complex component-local
 * state in `CalendarScreen` (active tab, calendar/room view mode, selected date, selected
 * rooms, room-filter panel visibility) so it is updated through reducers and read from the
 * store (FR-016). The selected date is stored as an ISO string to keep the store
 * serializable.
 */

export interface CalendarUiState {
  activeTab: string;
  view: string;
  roomView: string;
  dateIso: string | null;
  selectedRooms: string[];
  roomFilterOpen: boolean;
}

const initialState: CalendarUiState = {
  activeTab: '',
  view: '',
  roomView: '',
  dateIso: null,
  selectedRooms: [],
  roomFilterOpen: false,
};

export const calendarUiSlice = createSlice({
  name: 'calendarUi',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setView: (state, action: PayloadAction<string>) => {
      state.view = action.payload;
    },
    setRoomView: (state, action: PayloadAction<string>) => {
      state.roomView = action.payload;
    },
    setDateIso: (state, action: PayloadAction<string | null>) => {
      state.dateIso = action.payload;
    },
    setSelectedRooms: (state, action: PayloadAction<string[]>) => {
      state.selectedRooms = action.payload;
    },
    setRoomFilterOpen: (state, action: PayloadAction<boolean>) => {
      state.roomFilterOpen = action.payload;
    },
    resetCalendarUi: () => initialState,
  },
});

export const {
  setActiveTab,
  setView,
  setRoomView,
  setDateIso,
  setSelectedRooms,
  setRoomFilterOpen,
  resetCalendarUi,
} = calendarUiSlice.actions;

export const calendarUiReducer = calendarUiSlice.reducer;

export const selectCalendarActiveTab = (state: RootState): string => state.calendarUi.activeTab;
export const selectCalendarView = (state: RootState): string => state.calendarUi.view;
export const selectCalendarRoomView = (state: RootState): string => state.calendarUi.roomView;
export const selectCalendarDateIso = (state: RootState): string | null => state.calendarUi.dateIso;
export const selectCalendarSelectedRooms = (state: RootState): string[] =>
  state.calendarUi.selectedRooms;
export const selectCalendarRoomFilterOpen = (state: RootState): boolean =>
  state.calendarUi.roomFilterOpen;
